import { BitmapCoordinatesRenderingScope } from 'fancy-canvas'
import DrawingBase from '../drawing.base'
import { DrawingOptions, DrawingSettingGroup, DrawingTypes, SettingValues } from '../drawing.interface'
import { MouseEventParams, PrimitiveHoveredItem, Time } from 'lightweight-charts'
import { scaledCoordinate } from '../drawing.helper'
import { ChartEvents } from '../../chart.events'
import { Order, TransactionType } from '../../../../proto/generated/order'
import { alpha } from '@mui/material'
import { getTimeIndex } from '../../../../util/chart'

export type orderMarkers = 'orderMarkers'

export enum OrderComponentTypes {
    SELL_ORDER = 'SELL_ORDER',
    BUY_ORDER = 'BUY_ORDER'
}

export type OrdersMarkersDrawingState = {
    orders: Record<number, Order[]>
}


export type OrdersMarkersDrawingOptions = {
    drawingType: orderMarkers
}

export type OrdersMarkersDrawingSettings = {
    orders: Record<number, Order[]>
}

class OrderMarkersDrawing extends DrawingBase<OrdersMarkersDrawingOptions, OrdersMarkersDrawingSettings, OrdersMarkersDrawingState> {
    initialPoint = 0
    fixedDrawing: boolean = true;
    baseSize = 10;
    hoveredKey: string | null = null
    groupedOrders: Record<number, {
        buy: {
            orders: Order[]
            totalQuantity: number
            averagePrice: number
        }
        sell: {
            orders: Order[]
            totalQuantity: number
            averagePrice: number
        }
    }> = {}
    groupedOrdersDrawn: Record<number, {
        buy: {
            x1: number
            x2: number
            y1: number
            y2: number
        }
        sell: {
            x1: number
            x2: number
            y1: number
            y2: number
        }
    }> = {}

    constructor(options: DrawingOptions<any, any, any>) {
        super(options)
        if (this._chartApi) this._chartApi.subscribeClick(this.handleChartClick.bind(this))
    }

    private findHoveredData(x: number, y: number): {
        x: number
        y: number
        type: 'buy' | 'sell'
        time: number
        orders: Order[]
        totalQuantity: number
        averagePrice: number
    } | null {
        const sx = scaledCoordinate(x, this.horizontalPixelRatio)
        const sy = scaledCoordinate(y, this.verticalPixelRatio)
        if (!this.groupedOrdersDrawn) {
            this.hoveredKey = null
            return null
        }

        const timeKeys = Object.keys(this.groupedOrdersDrawn).map(t => Number(t))

        if (timeKeys.length === 0) {
            this.hoveredKey = null
            return null
        }

        for (let i = 0; i < timeKeys.length; i++) {
            const t = timeKeys[i]
            const d = this.groupedOrders?.[t]
            if (!d) {
                continue
            }

            const drawn = this.groupedOrdersDrawn?.[t]
            if (!drawn) {
                continue
            }

            // Check buy area
            if (drawn.buy) {
                if (sx >= drawn.buy.x1 && sx <= drawn.buy.x2 && sy >= drawn.buy.y1 && sy <= drawn.buy.y2) {
                    this.hoveredKey = `${t}:up`
                    return {
                        x: drawn.buy.x2 / this.horizontalPixelRatio,
                        y: drawn.buy.y1 / this.verticalPixelRatio,
                        type: 'buy',
                        time: this.getSeries().dataByIndex(t)?.time as number,
                        ...d.buy
                    }
                }
            }

            // Check sell area
            if (drawn.sell) {
                if (sx >= drawn.sell.x1 && sx <= drawn.sell.x2 && sy >= drawn.sell.y1 && sy <= drawn.sell.y2) {
                    this.hoveredKey = `${t}:down`
                    return {
                        x: drawn.sell.x2 / this.horizontalPixelRatio,
                        y: drawn.sell.y1 / this.verticalPixelRatio,
                        type: 'sell',
                        time: this.getSeries().dataByIndex(t)?.time as number,
                        ...d.sell
                    }
                }
            }
        }

        this.hoveredKey = null
        return null
    }

    private handleChartClick(param: MouseEventParams): void {
        const { point } = param
        if (!point) return
        const clickData = this.findHoveredData(point.x, point.y)
        if (clickData) {
            this._chart.publishEvent(ChartEvents.CHART_ORDER_MARKER_CLICKED, clickData);
        } else {
            this._chart.publishEvent(ChartEvents.CHART_ORDER_MARKER_CLICKED, null);
        }
    }

    draw(scope: BitmapCoordinatesRenderingScope) {
        const ctx = scope.context;
        const { orders } = this._state;
        if (!orders) return

        const visibleLogicalRange = this._chartApi.timeScale().getVisibleLogicalRange()
        if (!visibleLogicalRange) return
        const barsInfo = this.getSeries().barsInLogicalRange(visibleLogicalRange)
        if (!barsInfo) return
        ctx.globalCompositeOperation = 'source-over'
        const primaryData = this._chart.getRenderedPrimaryData()
        if (!primaryData) return

        const markerThreshold = scaledCoordinate(10, this.verticalPixelRatio)
        const arrowSize = scaledCoordinate(10, this.verticalPixelRatio)
        this.calculateGroupedData()
        const groupedOrders = this.groupedOrders


        Object.keys(groupedOrders).filter(v=>!isNaN(Number(v))).forEach((index) => {
            if (index === undefined || Number(index) < 0) return
            const i = Number(index)
            const timeCoord = this._chartApi.timeScale().timeToCoordinate(primaryData[i]?.time)
            if (timeCoord == null) return

            const xCoordinate = scaledCoordinate(timeCoord, this.horizontalPixelRatio)
            if (!xCoordinate || !ctx) return

            let highPriceCoord = this.priceToCoordinate(primaryData[index]?.high ?? 0) as number
            let lowPriceCoord = this.priceToCoordinate(primaryData[index]?.low ?? 0) as number
            if (!highPriceCoord || !lowPriceCoord) return
            highPriceCoord = scaledCoordinate(highPriceCoord, this.verticalPixelRatio)
            highPriceCoord = highPriceCoord - markerThreshold

            lowPriceCoord = scaledCoordinate(lowPriceCoord, this.verticalPixelRatio)
            lowPriceCoord = lowPriceCoord + markerThreshold

            const hoveredUp = this.hoveredKey === `${index}:up`
            const hoveredDown = this.hoveredKey === `${index}:down`

            if (groupedOrders[i].buy.orders.length) {
                this.drawStackedArrows(ctx, xCoordinate, lowPriceCoord, groupedOrders[i].buy.orders.length, arrowSize, this._chart.theme.palette.buy.main, 'up', hoveredUp, i)
            }
            if (groupedOrders[i].sell.orders.length) {
                this.drawStackedArrows(ctx, xCoordinate, highPriceCoord, groupedOrders[i].sell.orders.length, arrowSize, this._chart.theme.palette.sell.main, 'down', hoveredDown, i)
            }
        })
    }

    hitTest(x: number, y: number): PrimitiveHoveredItem | null {
        const hoveredData = this.findHoveredData(x, y);
        if (hoveredData) {
            return {
                externalId: this.id,
                zOrder: 'top',
                cursorStyle: 'pointer',
            };
        }
        return null
    }

    updateHandle = (id: string, time: Time, price: number) => {

    }

    calculateGroupedData = () => {
        const groupedOrders: Record<number, {
            buy: {
                orders: Order[]
                totalQuantity: number
                averagePrice: number
            }
            sell: {
                orders: Order[]
                totalQuantity: number
                averagePrice: number
            }
        }> = {}
        const { orders } = this._state;
        const primaryData = this._chart.getRenderedPrimaryData()
        if (!primaryData) return

        Object.keys(orders).forEach(orderTime => {
            if (!orderTime) return
            const time = Number(orderTime)
            const index = getTimeIndex(primaryData, time)
            if (index === undefined || index < 0) return
            const buyOrders: Order[] = []
            const sellOrders: Order[] = []
            orders[orderTime].forEach((o: Order) => {
                if (o.transactionType === TransactionType.BUY) {
                    buyOrders.push(o)
                } else if (o.transactionType === TransactionType.SELL) {
                    sellOrders.push(o)
                }
            })
            if (groupedOrders[index]) {
                groupedOrders[index] = {
                    buy: {
                        orders: [...groupedOrders[index].buy.orders, ...buyOrders],
                        totalQuantity: 0,
                        averagePrice: 0
                    },
                    sell: {
                        orders: [...groupedOrders[index].sell.orders, ...sellOrders],
                        totalQuantity: 0,
                        averagePrice: 0
                    }
                }
            } else {
                groupedOrders[index] = {
                    buy: {
                        orders: buyOrders,
                        totalQuantity: 0,
                        averagePrice: 0
                    },
                    sell: {
                        orders: sellOrders,
                        totalQuantity: 0,
                        averagePrice: 0
                    }
                }
            }
        })
        Object.keys(groupedOrders).forEach(key => {
            const timestamp = Number(key)
            if (!timestamp) return
            let totalBuyQuantity = 0
            let totalBuyPrice = 0
            groupedOrders[timestamp].buy.orders.forEach((order) => {
                totalBuyQuantity += order.quantity
                totalBuyPrice += (order.averageTradedPrice ? order.averageTradedPrice * order.quantity : 0)
            })
            const averageBuyPrice = totalBuyQuantity > 0 ? (totalBuyPrice / totalBuyQuantity) : 0
            groupedOrders[timestamp].buy.totalQuantity = totalBuyQuantity
            groupedOrders[timestamp].buy.averagePrice = averageBuyPrice

            let totalSellQuantity = 0
            let totalSellPrice = 0
            groupedOrders[timestamp].sell.orders.forEach((order) => {
                totalSellQuantity += order.quantity
                totalSellPrice += (order.averageTradedPrice ? order.averageTradedPrice * order.quantity : 0)
            })
            const averageSellPrice = totalSellQuantity > 0 ? (totalSellPrice / totalSellQuantity) : 0
            groupedOrders[timestamp].sell.totalQuantity = totalSellQuantity
            groupedOrders[timestamp].sell.averagePrice = averageSellPrice

        })
        this.groupedOrders = groupedOrders
    }

    onSettingsUpdate = (settings: Partial<OrdersMarkersDrawingSettings>) => {
        if (settings.orders) {
            this._state.orders = settings.orders
        }
        this._settings = {
            ...this._settings,
            ...settings
        }
        this.primitive?.requestUpdate()
    }


    getAvailableSettings = (): DrawingSettingGroup<OrdersMarkersDrawingSettings, SettingValues>[] => {
        return []
    }

    getDrawingType(): DrawingTypes {
        switch (this.options.drawingType) {
            case 'orderMarkers':
                return 'orderMarkers'
        }
    }

    getSettingsValues(): OrdersMarkersDrawingSettings {
        return { ...this._settings }
    }

    getMiniSettings = () => {
        return []
    }

    // Draw rounded rect path
    drawRoundedRect(
        ctx: CanvasRenderingContext2D,
        x: number,
        y: number,
        width: number,
        height: number,
        radius: number,
        radiusOptions = { topLeft: true, topRight: true, bottomRight: true, bottomLeft: true }
    ) {
        const r = {
            tl: radiusOptions.topLeft ? radius : 0,
            tr: radiusOptions.topRight ? radius : 0,
            br: radiusOptions.bottomRight ? radius : 0,
            bl: radiusOptions.bottomLeft ? radius : 0,
        };

        ctx.beginPath();
        ctx.moveTo(x + r.tl, y);
        ctx.lineTo(x + width - r.tr, y);
        if (r.tr) ctx.quadraticCurveTo(x + width, y, x + width, y + r.tr);
        else ctx.lineTo(x + width, y);

        ctx.lineTo(x + width, y + height - r.br);
        if (r.br) ctx.quadraticCurveTo(x + width, y + height, x + width - r.br, y + height);
        else ctx.lineTo(x + width, y + height);

        ctx.lineTo(x + r.bl, y + height);
        if (r.bl) ctx.quadraticCurveTo(x, y + height, x, y + height - r.bl);
        else ctx.lineTo(x, y + height);

        ctx.lineTo(x, y + r.tl);
        if (r.tl) ctx.quadraticCurveTo(x, y, x + r.tl, y);
        else ctx.lineTo(x, y);

        ctx.closePath();
    }

    drawAveragePriceArrow(
        ctx: CanvasRenderingContext2D,
        x: number,
        y: number,
        size: number,
        color: string,
        direction: 'left' | 'right'
    ) {
        const wingSize = size / 2;
        const shaftWidth = scaledCoordinate(2, this.verticalPixelRatio);
        const extraOutline = shaftWidth / 2.3

        // ---- OUTLINE ----
        ctx.strokeStyle = this._chart.theme.palette.background.default;
        ctx.lineWidth = shaftWidth * 2;   // slightly thicker for outline
        ctx.lineJoin = 'round';

        ctx.beginPath();
        if (direction === 'left') {
            ctx.moveTo(x - wingSize - extraOutline, y - wingSize - extraOutline);
            ctx.lineTo(x, y);
            ctx.lineTo(x - wingSize - extraOutline, y + wingSize + extraOutline);
        } else {
            ctx.moveTo(x + wingSize + extraOutline, y + wingSize + extraOutline);
            ctx.lineTo(x, y);
            ctx.lineTo(x + wingSize + extraOutline, y - wingSize - extraOutline);
        }
        ctx.stroke();

        // ---- MAIN ARROW ----
        ctx.strokeStyle = color;
        ctx.lineWidth = shaftWidth;

        ctx.beginPath();
        if (direction === 'left') {
            ctx.moveTo(x - wingSize, y - wingSize);
            ctx.lineTo(x, y);
            ctx.lineTo(x - wingSize, y + wingSize);
        } else {
            ctx.moveTo(x + wingSize, y + wingSize);
            ctx.lineTo(x, y);
            ctx.lineTo(x + wingSize, y - wingSize);
        }
        ctx.stroke();

    }

    drawArrow(
        ctx: CanvasRenderingContext2D,
        x: number,
        y: number,
        size: number,
        color: string,
        drawShaft = false,
        direction: 'up' | 'down'
    ) {
        const wingSize = size / 2;
        const shaftHeight = size * 0.9;
        const shaftWidth = scaledCoordinate(2, this.verticalPixelRatio);
        const extraOutline = shaftWidth / 2.3

        // ---- OUTLINE ----
        ctx.strokeStyle = this._chart.theme.palette.background.default;
        ctx.lineWidth = shaftWidth * 2;   // slightly thicker for outline
        ctx.lineJoin = 'round';

        ctx.beginPath();
        if (direction === 'up') {
            ctx.moveTo(x - wingSize - extraOutline, y + wingSize + extraOutline);
            ctx.lineTo(x, y);
            ctx.lineTo(x + wingSize + extraOutline, y + wingSize + extraOutline);
        } else {
            ctx.moveTo(x - wingSize - extraOutline, y - wingSize - extraOutline);
            ctx.lineTo(x, y);
            ctx.lineTo(x + wingSize + extraOutline, y - wingSize - extraOutline);
        }
        ctx.stroke();

        if (drawShaft) {
            ctx.beginPath();
            ctx.moveTo(x, y);
            ctx.lineTo(x, direction === 'up' ? y + wingSize + shaftHeight + extraOutline : y - wingSize - shaftHeight- extraOutline);
            ctx.stroke();
        }

        ctx.strokeStyle = color;
        ctx.lineWidth = shaftWidth;
        ctx.lineJoin = 'round';

        // Arrowhead (fat V)
        ctx.beginPath();
        if (direction === 'up') {
            ctx.moveTo(x - wingSize, y + wingSize);
            ctx.lineTo(x, y);
            ctx.lineTo(x + wingSize, y + wingSize);
        } else {
            ctx.moveTo(x - wingSize, y - wingSize);
            ctx.lineTo(x, y);
            ctx.lineTo(x + wingSize, y - wingSize);
        }
        ctx.stroke();

        // Shaft
        if (drawShaft) {
            ctx.beginPath();
            ctx.strokeStyle = color;
            ctx.lineWidth = shaftWidth;
            ctx.moveTo(x, y);
            ctx.lineTo(x, direction === 'up' ? y + wingSize + shaftHeight : y - wingSize - shaftHeight);
            ctx.stroke();
        }
    }

    drawStackedArrows(ctx: CanvasRenderingContext2D, x: number, y: number, count: number, size: number, color: string, direction: 'up' | 'down', hovered: boolean = false, index: number) {
        const spacing = scaledCoordinate(5, this.verticalPixelRatio)
        const radius = scaledCoordinate(6, this.verticalPixelRatio)

        if (direction === 'up') {
            this.groupedOrdersDrawn[index] = {
                ...(this.groupedOrdersDrawn?.[index] ?? {} as any),
                buy: {
                    x1: x - size / 2 - spacing,
                    x2: x + size / 2 + spacing,
                    y1: y - spacing / 2,
                    y2: y - spacing / 2 + size + spacing * 2 + (spacing * (count - 1))
                }
            }
        } else {
            this.groupedOrdersDrawn[index] = {
                ...(this.groupedOrdersDrawn?.[index] ?? {} as any),
                sell: {
                    x1: x - size / 2 - spacing,
                    x2: x + size / 2 + spacing,
                    y1: y - spacing / 2 - (size + spacing + (spacing * (count - 1))),
                    y2: y - spacing / 2 + spacing
                }
            }
        }

        if (hovered) {
            this.drawRoundedRect(ctx,
                x - size / 2 - spacing,
                y - spacing / 2 - (direction === 'down' ? size + spacing + (spacing * (count - 1)) : 0),
                size + spacing * 2,
                size + spacing * 2 + (spacing * (count - 1)),
                radius
            )
            ctx.fillStyle = alpha(color, 0.2);
            ctx.fill();
        }

        for (let i = 0; i < count; i++) {
            const offset = i * (spacing) * (direction === 'up' ? 1 : -1);
            this.drawArrow(ctx, x, y + offset, size, color, i === count - 1, direction);
        }

        if (hovered) {
            let averagePrice = direction === 'up' ? this.groupedOrders[index].buy.averagePrice : this.groupedOrders[index].sell.averagePrice
            averagePrice = scaledCoordinate(this.priceToCoordinate(averagePrice / (this._chart.securities?.[0]?.priceMultiplier ?? 100)) as number, this.verticalPixelRatio)
            this.drawAveragePriceArrow(
                ctx,
                x,
                averagePrice,
                size,
                color,
                direction === 'up' ? 'left' : 'right'
            )
        }
    }

    destroy(publish?: boolean, pushToHistory?: boolean, chartTypeToHistory?: boolean): void {
        this._chartApi.unsubscribeClick(this.handleChartClick.bind(this))
        super.destroy()
    }
}

export default OrderMarkersDrawing
